import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";
import { Link, List, Stack, ListItem, Box, Typography } from "@mui/material";

import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import { createState } from "@/global/store/zustand_store";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select a minimum 1 game to continue.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect your Philadelphia 76ers Account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the suites you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://am.ticketmaster.com/sixers/"
        >
          Philadelphia 76ers Account
        </Link>
        . Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "What is XfinityMobileArenaSuites.com?",
    description:
      "XfinityMobileArenaSuites.com is the official single event suites website of Xfinity Mobile Arena. XfinityMobileArenaSuites.com is designed specifically for premium sales (e.g. luxury suites, luxury suite tickets, etc.).",
  },
  {
    title: "When do I get my tickets after purchasing a suite?",
    description:
      "After purchase, a representative from Xfinity Mobile Arena Premium Partnership Team will be in touch to coordinate ticket delivery. All tickets and parking passes will be mobile-only.",
  },
  {
    title: "How are my tickets delivered?",
    description:
      "All suite tickets and parking passes will be mobile-only. A representative will provide instructions on how to access and download your tickets. Tickets cannot be printed and must be presented on your mobile phone.",
  },
  {
    title: "Will the suite be private to my group?",
    description: "Yes. All suites listed are fully private.",
  },
  {
    title: "What forms of payment are accepted?",
    description:
      "Acceptable forms of payment include all major credit cards (Visa, Mastercard, American Express, and Discover).",
  },
  {
    title: "Is payment required in full?",
    description:
      "Yes — payment in full is required upon booking to secure a suite.",
  },
  {
    title: "Are there any additional fees?",
    description:
      "There are no additional fees when purchasing the suite. The only other costs would be additions to catering, additional ticket purchases, or other purchases made at the arena.",
  },
  {
    title: "What if my event is postponed/rescheduled?",
    description:
      "If an event is postponed, your suite tickets will be valid for the rescheduled event.",
  },
  {
    title: "How and when do I order catering?",
    description: (
      <>
        A link to order your catering will be sent with your mobile tickets.
        Catering needs to be placed four (4) business days prior to your event.
        For questions, contact Aramark at{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:xmasuites@aramark.com"
        >
          xmasuites@aramark.com
        </Link>{" "}
        or call{" "}
        <Link
          href="tel:2159525251"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          215-952-5251
        </Link>
        .
      </>
    ),
  },
  {
    title:
      "I booked a suite, but I haven't received my tickets yet. Where are they?",
    description: (
      <>
        Contact a representative from the Premium Partnership Team at{" "}
        <Link
          href="tel:2153899500"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          215-389-9500
        </Link>
        .
      </>
    ),
  },
  {
    title: "If I have additional questions, where can I find more information?",
    description: (
      <>
        If you have any additional questions, view a more detailed set of FAQs
        at{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="https://www.xfinitymobilearenasuites.com/faq/"
        >
          xfinitymobilearenasuites.com/faq
        </Link>
        .
      </>
    ),
  },
];

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const clubSuiteSuiteNames = Array.from(
  { length: 18 },
  (_, i) => `Club Suite ${String.fromCharCode(65 + i)}`,
);

export const libertyLoftSuiteNames = Array.from(
  { length: 5 },
  (_, i) => `Liberty Loft ${i + 1}`,
);

export const Philadelphia76ersInventoryCard = () => {
  const modal = inventoryCardSuiteModal();

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const AssemblyRoomLoftContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Assembly Room Loft
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 12 Tickets Included
          <br />
          • Up To 12 Additional Tickets Available
          <br />
          • 4 Parking Passes Included
          <br />
          • Food & Beverage Included
          <br />• Private Bathroom
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const HyundaiLuxurySuitesContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Hyundai Luxury Suites
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 12-15 Tickets Included
          <br />
          • 3-6 Additional Tickets Available
          <br />
          • 4 Parking Passes Included
          <br />
          • Food & Beverage Included
          <br />• Private Bathroom
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const LibertyLoftContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Liberty Loft
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 12 Tickets Included
          <br />
          • Up To 18 Additional Tickets Available
          <br />
          • 4 Parking Passes Included
          <br />• Food & Beverage Included
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const ClubSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Club Suite
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 12-15 Tickets Included
          <br />
          • 3-6 Additional Tickets Available
          <br />
          • 4 Parking Passes Included
          <br />
          • Food & Beverage Included
          <br />• Private Bathroom
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const ClubLogeContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Club Loge
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">• 4 Tickets Included</Typography>
      </ListItem>
    </List>
  </Box>
);

export const PrimepointLoungeContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Primepoint Lounge
      </Typography>
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • 2-122 Tickets Included
          <br />• Food & Beverage Included
        </Typography>
      </ListItem>
    </List>
  </Box>
);
